
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { ShoppingCart, Package } from "lucide-react";
import { PurchaseRequestCard } from "./purchase-requests/PurchaseRequestCard";
import { PurchaseRequestDetailsDialog } from "./purchase-requests/PurchaseRequestDetailsDialog";
import { usePurchaseRequestStatusUpdate } from "./purchase-requests/hooks/usePurchaseRequestStatusUpdate";
import { usePurchaseRequestDeletion } from "./purchase-requests/hooks/usePurchaseRequestDeletion";

export const PurchaseRequestsTabNew = () => {
  const { session } = useAuth();
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  // Utiliser les nouveaux hooks
  const updateRequestStatus = usePurchaseRequestStatusUpdate();
  const deleteRequest = usePurchaseRequestDeletion();

  const { data: purchaseRequests, isLoading } = useQuery({
    queryKey: ["purchase-requests", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) {
        console.log('No user session found');
        return [];
      }

      console.log('Fetching purchase requests for user:', session.user.id);

      const { data, error } = await supabase
        .from("product_purchase_requests")
        .select(`
          *,
          product:professional_products!fk_product(*),
          buyer:profiles!product_purchase_requests_buyer_id_fkey(*)
        `)
        .eq("professional_id", session.user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error('Error fetching purchase requests:', error);
        throw error;
      }

      console.log('Fetched purchase requests:', data);
      return data || [];
    },
    enabled: !!session?.user?.id,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Approuvée';
      case 'pending': return 'En attente';
      case 'rejected': return 'Refusée';
      default: return status;
    }
  };

  const handleViewDetails = (request: any) => {
    console.log('Opening details for purchase request:', request);
    setSelectedRequest(request);
    setIsDetailDialogOpen(true);
  };

  const handleStatusUpdate = (requestId: string, status: string) => {
    console.log('Handling status update:', { requestId, status });
    updateRequestStatus.mutate({ requestId, status });
  };

  const handleDelete = (requestId: string) => {
    console.log('Handling purchase request deletion:', requestId);
    deleteRequest.mutate(requestId);
  };

  // Séparer les demandes en attente et les autres
  const pendingRequests = purchaseRequests?.filter(req => req.status === 'pending') || [];
  const otherRequests = purchaseRequests?.filter(req => req.status !== 'pending') || [];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section des demandes en attente */}
      {pendingRequests.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <ShoppingCart className="h-5 w-5" />
              Demandes d'achat en attente ({pendingRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <PurchaseRequestCard
                  key={request.id}
                  request={request}
                  onStatusUpdate={handleStatusUpdate}
                  onViewDetails={handleViewDetails}
                  onDelete={handleDelete}
                  isUpdating={updateRequestStatus.isPending}
                  isDeleting={deleteRequest.isPending}
                  getStatusColor={getStatusColor}
                  getStatusText={getStatusText}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section de toutes les demandes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Toutes les demandes d'achat ({purchaseRequests?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!purchaseRequests || purchaseRequests.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucune demande d'achat trouvée</p>
            </div>
          ) : (
            <div className="space-y-4">
              {[...pendingRequests, ...otherRequests].map((request) => (
                <PurchaseRequestCard
                  key={request.id}
                  request={request}
                  onStatusUpdate={handleStatusUpdate}
                  onViewDetails={handleViewDetails}
                  onDelete={handleDelete}
                  isUpdating={updateRequestStatus.isPending}
                  isDeleting={deleteRequest.isPending}
                  getStatusColor={getStatusColor}
                  getStatusText={getStatusText}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog pour afficher les détails de la demande d'achat */}
      <PurchaseRequestDetailsDialog
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
        request={selectedRequest}
        getStatusColor={getStatusColor}
        getStatusText={getStatusText}
        onStatusUpdate={handleStatusUpdate}
        onDelete={handleDelete}
        isUpdating={updateRequestStatus.isPending}
        isDeleting={deleteRequest.isPending}
      />
    </div>
  );
};
